import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Target, Trophy, Calendar, ArrowRight, Sparkles, CheckCircle, Circle, Clock, Moon, BookOpen, Rocket, X, PartyPopper, RefreshCw, Heart, Menu, LayoutDashboard, Settings, LogOut, Lock, AlertTriangle, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';
import HabitTracker from '@/components/HabitTracker';
import JournalPreview from '@/components/JournalPreview';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_NAV = "/images/dea-wordmark.png"; // Small "a.gpt" logo for navbar
const LOGO_HERO = "/images/dea-wordmark.png"; // Full "Dea — Version 3.0" logo for page content

// 90-Day Journey Phase Info
const phaseInfo = {
  alignment: {
    name: 'Alignment Phase',
    weeks: '1-4',
    description: 'Clarity, habits & becoming HER',
    focus: 'Clarity, Habits & Mindset',
    color: 'from-[#87A6A7] to-[#6B9394]'
  },
  amplification: {
    name: 'Amplification Phase',
    weeks: '5-8',
    description: 'Momentum building, skill development, and executing strategy.',
    focus: 'Action & momentum',
    color: 'from-[#CAAB84] to-[#B4941F]'
  },
  actualization: {
    name: 'Actualization Phase',
    weeks: '9-12',
    description: 'Final push, integration, celebration, and next-level planning.',
    focus: 'Completion & celebration',
    color: 'from-[#AA9283] to-[#8A7263]'
  }
};

// Cycle Phase Configuration with brand colors
const cyclePhaseConfig = {
  menstruation: {
    name: 'Menstruation',
    days: [1, 7],
    color: '#B1897C',
    bgColor: 'bg-[#B1897C]',
    lightBg: 'bg-[#B1897C]/10',
    borderColor: 'border-[#B1897C]',
    textColor: 'text-[#B1897C]'
  },
  follicular: {
    name: 'Follicular',
    days: [8, 14],
    color: '#CAAB84',
    bgColor: 'bg-[#CAAB84]',
    lightBg: 'bg-[#CAAB84]/10',
    borderColor: 'border-[#CAAB84]',
    textColor: 'text-[#CAAB84]'
  },
  ovulation: {
    name: 'Ovulation',
    days: [15, 21],
    color: '#87A6A7',
    bgColor: 'bg-[#87A6A7]',
    lightBg: 'bg-[#87A6A7]/10',
    borderColor: 'border-[#87A6A7]',
    textColor: 'text-[#87A6A7]'
  },
  luteal: {
    name: 'Luteal',
    days: [22, 28],
    color: '#AA9283',
    bgColor: 'bg-[#AA9283]',
    lightBg: 'bg-[#AA9283]/10',
    borderColor: 'border-[#AA9283]',
    textColor: 'text-[#AA9283]'
  }
};

// Get phase for a specific day
const getPhaseForDay = (day) => {
  if (day <= 7) return 'menstruation';
  if (day <= 14) return 'follicular';
  if (day <= 21) return 'ovulation';
  return 'luteal';
};

export default function Dashboard() {
  const { user, getAuthHeader, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [cyclePhase, setCyclePhase] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [journeyForm, setJourneyForm] = useState({
    main_goal: '',
    supporting_goals: ['', '', '']
  });
  const [startingJourney, setStartingJourney] = useState(false);
  const [taskProgress, setTaskProgress] = useState({ total: 0, completed: 0, progress_percent: 0 });
  const [milestones, setMilestones] = useState({ milestones: [], completed: 0, total: 12, progress_percent: 0 });
  const [pendingRoadmap, setPendingRoadmap] = useState(null);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [showDay90Modal, setShowDay90Modal] = useState(false);
  const [processingCompletion, setProcessingCompletion] = useState(false);
  const [dailyInspiration, setDailyInspiration] = useState(null);
  
  // Trial access state
  const [accessLevel, setAccessLevel] = useState(null);
  const [showGlowUpModal, setShowGlowUpModal] = useState(false);
  const [deaStreak, setDeaStreak] = useState(0);

  const fetchData = async () => {
    try {
      // Get user's timezone from browser
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const [journeyRes, cycleRes, dateRes, taskRes, milestonesRes, pendingRes, inspirationRes, pendingTasksRes, accessRes, streakRes] = await Promise.all([
        axios.get(`${API}/journey/current?tz=${encodeURIComponent(userTimezone)}`, { headers: getAuthHeader() }),
        axios.get(`${API}/user/cycle-phase?tz=${encodeURIComponent(userTimezone)}`, { headers: getAuthHeader() }),
        axios.get(`${API}/current-date?tz=${encodeURIComponent(userTimezone)}`),
        axios.get(`${API}/tasks/progress`, { headers: getAuthHeader() }),
        axios.get(`${API}/journey/milestones`, { headers: getAuthHeader() }),
        axios.get(`${API}/journey/pending-roadmap`, { headers: getAuthHeader() }),
        axios.get(`${API}/user/daily-inspiration`, { headers: getAuthHeader() }),
        axios.get(`${API}/tasks/pending`, { headers: getAuthHeader() }),
        axios.get(`${API}/user/access-level`, { headers: getAuthHeader() }),
        axios.get(`${API}/dashboard/streak`, { headers: getAuthHeader() }).catch(() => ({ data: { streak: 0 } }))
      ]);
      setJourney(journeyRes.data.journey);
      if (cycleRes.data.enabled) {
        setCyclePhase(cycleRes.data.phase);
      }
      setCurrentDate(dateRes.data);
      setTaskProgress(taskRes.data);
      setMilestones(milestonesRes.data);
      setDailyInspiration(inspirationRes.data);
      setPendingTasks(pendingTasksRes.data.tasks || []);
      setAccessLevel(accessRes.data);
      setDeaStreak(streakRes.data?.streak || 0);
      
      // Show glow up welcome modal for full access users who haven't seen it
      if (accessRes.data?.type === 'full' && !accessRes.data?.glowUpModalSeen) {
        setShowGlowUpModal(true);
      }
      
      // Check if there's a pending roadmap from Dea
      if (pendingRes.data.has_roadmap) {
        setPendingRoadmap(pendingRes.data);
      }
      
      // Log date to console for verification
      console.log('📅 Current Date:', dateRes.data.date);
      console.log('🕐 Current Time:', dateRes.data.time, dateRes.data.timezone_abbrev);
      console.log('🌍 User Timezone:', userTimezone);
      console.log('🔐 Access Level:', accessRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Check if it's Day 90 and show celebration modal
  useEffect(() => {
    if (journey && journey.current_day >= 90 && !journey.completed && !journey.maintenance_mode) {
      setShowDay90Modal(true);
    }
  }, [journey]);

  // Handle Day 90 completion options
  const handleStartNewJourney = async () => {
    setProcessingCompletion(true);
    try {
      // Complete current journey
      await axios.post(`${API}/journey/complete`, {}, { headers: getAuthHeader() });
      // Delete to start fresh
      await axios.delete(`${API}/journey/current`, { headers: getAuthHeader() });
      toast.success('Journey completed! Ready to start a new adventure! 🎉');
      setShowDay90Modal(false);
      updateUser({ onboarding_complete: false });
      await fetchData();
    } catch (error) {
      toast.error('Failed to process. Please try again.');
    } finally {
      setProcessingCompletion(false);
    }
  };

  const handleContinueSupport = async () => {
    setProcessingCompletion(true);
    try {
      await axios.post(`${API}/journey/continue`, {}, { headers: getAuthHeader() });
      toast.success('You\'re now in maintenance mode! Dea will continue supporting you. 💪');
      setShowDay90Modal(false);
      await fetchData();
    } catch (error) {
      toast.error('Failed to continue. Please try again.');
    } finally {
      setProcessingCompletion(false);
    }
  };

  const handleTakeBreak = async () => {
    setProcessingCompletion(true);
    try {
      await axios.post(`${API}/journey/complete`, {}, { headers: getAuthHeader() });
      toast.success('Journey completed! Your reflections are saved. Come back anytime! 🌟');
      setShowDay90Modal(false);
      await fetchData();
    } catch (error) {
      toast.error('Failed to process. Please try again.');
    } finally {
      setProcessingCompletion(false);
    }
  };

  // Start journey using Dea's roadmap from chat
  const handleStartFromChat = async () => {
    setStartingJourney(true);
    try {
      const response = await axios.post(`${API}/journey/start-from-chat`, {}, { headers: getAuthHeader() });
      toast.success('Your 90-day journey has begun! 🚀 Using the roadmap you created with Dea!');
      setPendingRoadmap(null);
      updateUser({ onboarding_complete: true, current_day: 1, current_phase: 'alignment' });
      await fetchData(); // Refresh journey data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start journey');
    } finally {
      setStartingJourney(false);
    }
  };

  const handleStartJourney = async () => {
    if (!journeyForm.main_goal.trim()) {
      toast.error('Please enter your main goal');
      return;
    }
    
    setStartingJourney(true);
    try {
      const goals = {
        main_goal: journeyForm.main_goal.trim(),
        supporting_goals: journeyForm.supporting_goals.filter(g => g.trim())
      };
      
      await axios.post(`${API}/journey/goal`, goals, { headers: getAuthHeader() });
      toast.success('Your 90-day journey has begun! 🚀');
      setShowJourneyModal(false);
      updateUser({ onboarding_complete: true, current_day: 1, current_phase: 'alignment' });
      await fetchData(); // Refresh journey data
    } catch (error) {
      toast.error('Failed to start journey');
    } finally {
      setStartingJourney(false);
    }
  };

  const currentDay = journey?.current_day || user?.current_day || 0;
  const currentPhase = journey?.current_phase || user?.current_phase || 'alignment';
  const progress = taskProgress.progress_percent || 0;
  const daysRemaining = journey?.days_remaining || (90 - currentDay);

  // Get current cycle day for calendar highlight
  const currentCycleDay = cyclePhase?.day || 0;

  // Cycle Calendar Component
  const CycleCalendar = ({ currentDay, phase }) => {
    const [hoveredDay, setHoveredDay] = useState(null);
    
    return (
      <div className="space-y-4">
        {/* Circular 28-day calendar */}
        <div className="relative w-full max-w-[280px] mx-auto aspect-square">
          {/* Center info */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center bg-[#FFFCF7] rounded-full w-24 h-24 flex flex-col items-center justify-center shadow-inner">
              <span className="text-3xl font-serif text-[#726158]">{currentDay}</span>
              <span className="text-xs text-[#AA9283]">of 28</span>
            </div>
          </div>
          
          {/* Days arranged in a circle */}
          {Array.from({ length: 28 }, (_, i) => {
            const day = i + 1;
            const dayPhase = getPhaseForDay(day);
            const config = cyclePhaseConfig[dayPhase];
            const angle = (i * (360 / 28) - 90) * (Math.PI / 180);
            const radius = 42; // percentage from center
            const x = 50 + radius * Math.cos(angle);
            const y = 50 + radius * Math.sin(angle);
            const isCurrentDay = day === currentDay;
            const isHovered = hoveredDay === day;
            
            return (
              <div
                key={day}
                className={`absolute w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all cursor-pointer
                  ${isCurrentDay 
                    ? `${config.bgColor} text-white ring-2 ring-offset-2 ring-[#726158] scale-110 shadow-lg` 
                    : `${config.lightBg} ${config.textColor} hover:scale-110`
                  }
                  ${isHovered && !isCurrentDay ? 'scale-110 shadow-md' : ''}
                `}
                style={{
                  left: `calc(${x}% - 14px)`,
                  top: `calc(${y}% - 14px)`
                }}
                onMouseEnter={() => setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
                title={`Day ${day} - ${config.name}`}
              >
                {day}
              </div>
            );
          })}
        </div>

        {/* Hovered day info */}
        {hoveredDay && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-sm text-[#AA9283]"
          >
            Day {hoveredDay} • {cyclePhaseConfig[getPhaseForDay(hoveredDay)].name} Phase
          </motion.div>
        )}

        {/* Phase Legend */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          {Object.entries(cyclePhaseConfig).map(([key, config]) => (
            <div 
              key={key} 
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${config.lightBg} ${
                phase === key ? `border-2 ${config.borderColor}` : ''
              }`}
            >
              <div className={`w-3 h-3 rounded-full ${config.bgColor}`} />
              <div className="text-xs">
                <span className={`font-medium ${config.textColor}`}>{config.name}</span>
                <span className="text-[#B1897C] ml-1">({config.days[0]}-{config.days[1]})</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Trial access helpers
  const isTrialUser = accessLevel?.type === 'trial';
  const isExpiredTrial = accessLevel?.type === 'expired_trial';
  const isFullAccess = accessLevel?.type === 'full' || (!accessLevel?.type && user?.paid);
  const trialDaysRemaining = accessLevel?.daysRemaining || 0;
  const upgradePrice = accessLevel?.upgradeAmount || 88;

  // Locked Section Overlay Component with Blurred Sneak Peek
  const LockedSection = ({ title, description, previewType }) => (
    <div className="absolute inset-0 z-10 rounded-2xl overflow-hidden">
      {/* Blurred Preview Content */}
      <div className="absolute inset-0 blur-[6px] opacity-60 pointer-events-none p-6">
        {previewType === 'roadmap' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-[#726158]">Your 90-Day Roadmap</h3>
              <span className="text-[#87A6A7] text-sm">Week 1 of 12</span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {[...Array(12)].map((_, i) => (
                <div key={i} className={`p-3 rounded-xl ${i === 0 ? 'bg-[#87A6A7]/30 border-2 border-[#87A6A7]' : 'bg-[#EAD8C2]/50'}`}>
                  <p className="text-xs text-[#AA9283] mb-1">Week {i + 1}</p>
                  <p className="text-sm text-[#726158] font-medium">{i < 4 ? 'Alignment' : i < 8 ? 'Amplification' : 'Actualization'}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-[#CAAB84]/10 rounded-xl">
              <p className="text-sm text-[#726158]">This week's focus: Building your foundation and getting crystal clear on your goals...</p>
            </div>
          </div>
        )}
        {previewType === 'milestones' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-[#726158]">12-Week Milestones</h3>
              <span className="text-[#87A6A7] text-sm">0 of 12 completed</span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="p-3 rounded-xl bg-[#EAD8C2]/50 text-center">
                  <div className="w-8 h-8 rounded-full border-2 border-[#CAAB84]/50 mx-auto mb-2 flex items-center justify-center">
                    <span className="text-[#CAAB84] text-sm">{i + 1}</span>
                  </div>
                  <p className="text-xs text-[#AA9283]">Week {i + 1}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Lock Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FFFCF7]/70 via-[#FFFCF7]/90 to-[#FFFCF7]/70 flex items-center justify-center">
        <div className="text-center p-6 bg-white/80 rounded-2xl shadow-lg border border-[#EAD8C2] max-w-xs">
          <div className="w-14 h-14 rounded-full bg-[#CAAB84]/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-[#CAAB84]" />
          </div>
          <h4 className="font-serif text-lg text-[#726158] mb-2">{title}</h4>
          <p className="text-sm text-[#AA9283] mb-4">{description}</p>
          <Link to="/checkout?upgrade=true">
            <Button className="btn-gold w-full" data-testid="locked-upgrade-btn">
              Unlock Full Access — ${upgradePrice}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );

  // Trial Banner Component - for active trial users
  const TrialBanner = () => {
    if (!isTrialUser) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#CAAB84] to-[#AA9283] rounded-2xl p-4 mb-6 text-white"
        data-testid="trial-banner"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Zap className="h-6 w-6" />
            </div>
            <div>
              <p className="font-serif text-lg">7-Day Power Start</p>
              <p className="text-white/90 text-sm">
                {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining • Upgrade to unlock your full 90-day journey
              </p>
            </div>
          </div>
          <Link to="/checkout?upgrade=true">
            <Button className="bg-white text-[#CAAB84] hover:bg-white/90 whitespace-nowrap" data-testid="trial-upgrade-btn">
              Upgrade Now — ${upgradePrice}
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  };

  // Expired Trial Banner - urgent messaging for expired trial users
  const ExpiredTrialBanner = () => {
    if (!isExpiredTrial) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#B1897C] to-[#AA9283] rounded-2xl p-5 mb-6 text-white border-2 border-[#B1897C]"
        data-testid="expired-trial-banner"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <div>
              <p className="font-serif text-xl">Your Trial Has Ended</p>
              <p className="text-white/90 text-sm mt-1">
                Don't lose your momentum! Upgrade now to continue your transformation journey with Dea.
              </p>
            </div>
          </div>
          <Link to="/checkout?upgrade=true">
            <Button className="bg-white text-[#B1897C] hover:bg-white/90 whitespace-nowrap py-3 px-6 text-base font-semibold" data-testid="expired-upgrade-btn">
              Continue My Journey — ${upgradePrice}
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCF7] flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Dashboard"
        description="Track your 90-day transformation journey with Dea. View your progress, milestones, and connect with Dea."
        noindex={true}
      />
      <div className="min-h-screen bg-[#FFFCF7]">
      {/* Journey Start Modal - Inlined to prevent re-render issues */}
      <AnimatePresence>
        {showJourneyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowJourneyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFCF7] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#CAAB84]/20 flex items-center justify-center">
                    <Rocket className="h-6 w-6 text-[#CAAB84]" />
                  </div>
                  <div>
                    <h2 className="font-serif text-xl text-[#726158]">Start Your 90-Day Journey</h2>
                    <p className="text-sm text-[#AA9283]">Let's get clear on your transformation</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowJourneyModal(false)}>
                  <X className="h-5 w-5 text-[#AA9283]" />
                </Button>
              </div>

              {/* Journey Phases Preview */}
              <div className="bg-[#EAD8C2]/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-[#726158] font-medium mb-3">Your 90-Day Transformation:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#87A6A7] flex items-center justify-center text-white text-xs font-bold">1</div>
                    <span className="text-sm text-[#726158]"><strong>Alignment</strong> (Weeks 1-4): Foundation & clarity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#CAAB84] flex items-center justify-center text-white text-xs font-bold">2</div>
                    <span className="text-sm text-[#726158]"><strong>Amplification</strong> (Weeks 5-8): Momentum & action</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#AA9283] flex items-center justify-center text-white text-xs font-bold">3</div>
                    <span className="text-sm text-[#726158]"><strong>Actualization</strong> (Weeks 9-12): Completion & celebration</span>
                  </div>
                </div>
              </div>

              {/* Main Goal Input */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#726158] block mb-2">
                    What's the ONE big goal you want to achieve in 90 days? *
                  </label>
                  <Textarea
                    value={journeyForm.main_goal}
                    onChange={e => setJourneyForm(prev => ({ ...prev, main_goal: e.target.value }))}
                    placeholder="E.g., Launch my online course, Hit $10K revenue month, Build my personal brand..."
                    className="input-light min-h-[80px]"
                    data-testid="journey-main-goal"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[#726158] block mb-2">
                    Supporting Goals (optional)
                  </label>
                  <p className="text-xs text-[#AA9283] mb-2">What smaller goals will help you reach your main goal?</p>
                  {journeyForm.supporting_goals.map((goal, i) => (
                    <Input
                      key={`support-goal-${i}`}
                      value={goal}
                      onChange={e => {
                        const newGoals = [...journeyForm.supporting_goals];
                        newGoals[i] = e.target.value;
                        setJourneyForm(prev => ({ ...prev, supporting_goals: newGoals }));
                      }}
                      placeholder={`Supporting goal ${i + 1}`}
                      className="input-light mb-2"
                      data-testid={`journey-support-goal-${i}`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowJourneyModal(false)}
                  className="flex-1 border-[#EAD8C2]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleStartJourney}
                  disabled={startingJourney || !journeyForm.main_goal.trim()}
                  className="flex-1 btn-gold"
                  data-testid="start-journey-btn"
                >
                  {startingJourney ? <span className="spinner" /> : <>Start My Journey <Rocket className="ml-2 h-4 w-4" /></>}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Day 90 Celebration Modal */}
      <AnimatePresence>
        {showDay90Modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFCF7] rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto text-center"
            >
              {/* Celebration Header */}
              <div className="mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#CAAB84] to-[#AA9283] flex items-center justify-center mx-auto mb-4">
                  <PartyPopper className="h-10 w-10 text-white" />
                </div>
                <h2 className="font-serif text-3xl text-[#726158] mb-2">🎉 DAY 90! 🎉</h2>
                <p className="text-[#AA9283] text-lg">You made it, sis! This is HUGE!</p>
              </div>

              {/* Journey Summary */}
              <div className="bg-[#EAD8C2]/30 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-[#AA9283] mb-1">Your 90-Day Glow Up Goal:</p>
                <p className="text-[#726158] font-medium">{journey?.main_goal}</p>
                <div className="mt-3 flex items-center gap-4 text-sm">
                  <span className="text-[#87A6A7]">✓ {milestones.completed}/12 Milestones</span>
                  <span className="text-[#CAAB84]">✓ {taskProgress.completed} Tasks Done</span>
                </div>
              </div>

              <p className="text-[#726158] mb-6">
                What would you like to do next? Your reflections and journals are always saved!
              </p>

              {/* Options */}
              <div className="space-y-3">
                <Button
                  onClick={handleStartNewJourney}
                  disabled={processingCompletion}
                  className="w-full btn-gold py-6"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Start a NEW 90-Day Journey
                </Button>
                
                <Button
                  onClick={handleContinueSupport}
                  disabled={processingCompletion}
                  variant="outline"
                  className="w-full border-[#87A6A7] text-[#87A6A7] hover:bg-[#87A6A7]/10 py-6"
                >
                  <Heart className="h-5 w-5 mr-2" />
                  Continue with Dea's Support
                  <span className="text-xs ml-2">(Maintenance Mode)</span>
                </Button>
                
                <Button
                  onClick={handleTakeBreak}
                  disabled={processingCompletion}
                  variant="ghost"
                  className="w-full text-[#AA9283] hover:bg-[#EAD8C2]/30 py-6"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Take a Break & Celebrate
                </Button>
              </div>

              <p className="text-xs text-[#B1897C] mt-4">
                Chat with Dea anytime to reflect on your journey!
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Glow Up Welcome Modal - One-time for full access users */}
      <AnimatePresence>
        {showGlowUpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            data-testid="glow-up-welcome-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFCF7] rounded-2xl p-6 sm:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <p className="font-serif text-2xl text-[#726158] mb-4">
                Hey {user?.name?.split(' ')[0]} <span className="text-[#CAAB84]">&#x1F49B;</span>
              </p>
              <div className="space-y-4 text-[#726158] text-sm sm:text-base leading-relaxed">
                <p>
                  This is Deon. I wanted to let you know that Dea just had her own glow up — and I think you're going to love what's new.
                </p>
                <p>
                  She is now your <strong>AI Glow Up Coach</strong>, trained across 5 areas of your life:
                </p>
                <div className="bg-[#EAD8C2]/30 rounded-xl p-4 space-y-2 text-sm">
                  <p><span className="text-[#CAAB84]">&#x2726;</span> <strong>Clarity</strong> — get clear on what you want</p>
                  <p><span className="text-[#87A6A7]">&#x2726;</span> <strong>Habits</strong> — build routines that make becoming HER inevitable</p>
                  <p><span className="text-[#AA9283]">&#x2726;</span> <strong>Mindset</strong> — push through what's keeping you stuck</p>
                  <p><span className="text-[#726158]">&#x2726;</span> <strong>Strategy</strong> — grow your business and income</p>
                  <p><span className="text-[#B1897C]">&#x2726;</span> <strong>Well-being</strong> — take care of yourself while you do it</p>
                </div>
                <div className="bg-[#CAAB84]/10 rounded-xl p-4 border border-[#CAAB84]/20">
                  <p className="font-semibold text-[#726158] mb-2">YOUR BUSINESS GOALS ARE SAFE.</p>
                  <p className="text-[#AA9283] text-sm">
                    Strategy is still one of Dea's 5 pillars. She will still hold you accountable to your revenue goals, your launches, and your content. She just sees the whole you now — not just the business side.
                  </p>
                </div>
                <p className="font-semibold text-[#726158]">WOULD YOU LIKE A FRESH START?</p>
                <p className="text-[#AA9283] text-sm">
                  You have two options — the choice is yours:
                </p>
                <div className="space-y-3 text-sm text-[#AA9283]">
                  <p>
                    <strong className="text-[#726158]">Option 1: Continue your current journey.</strong><br />
                    Your roadmap, milestones, and progress stay exactly as they are. Just keep going.
                  </p>
                  <p>
                    <strong className="text-[#726158]">Option 2: Start a new 90-day glow up.</strong><br />
                    If this feels like a new season, tell Dea: "I want to reset my 90-day journey." She'll walk you through it. Your journal and settings are always protected.
                  </p>
                </div>
                <p className="text-[#AA9283] text-sm italic">
                  There is no wrong answer.
                </p>
                <p className="text-[#AA9283] text-sm">
                  Now go chat with Dea. She's ready. <span className="text-[#CAAB84]">&#x1F49B;</span><br />
                  — Deon
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  className="btn-gold flex-1 py-5"
                  data-testid="glow-up-modal-continue"
                  onClick={async () => {
                    try { await axios.post(`${API}/user/dismiss-glow-up-modal`, {}, { headers: getAuthHeader() }); } catch (e) {}
                    setShowGlowUpModal(false);
                  }}
                >
                  Continue My Journey
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-[#87A6A7] text-[#87A6A7] hover:bg-[#87A6A7]/10 flex-1 py-5"
                  data-testid="glow-up-modal-reset"
                  onClick={async () => {
                    try { await axios.post(`${API}/user/dismiss-glow-up-modal`, {}, { headers: getAuthHeader() }); } catch (e) {}
                    setShowGlowUpModal(false);
                    navigate('/chat');
                  }}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Chat with Dea About a Reset
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#726158]/20 z-40"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              className="fixed left-0 top-0 bottom-0 w-72 bg-[#FFFCF7] border-r border-[#EAD8C2] z-50 flex flex-col"
            >
              <div className="p-4 border-b border-[#EAD8C2] flex items-center justify-between">
                <Link to="/">
                  <img src={LOGO_NAV} alt="Dea" className="h-10 w-auto object-contain" />
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(false)}
                  className="text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Daily Inspiration */}
              <div className="flex-1 p-4 overflow-y-auto">
                {(dailyInspiration?.affirmation || dailyInspiration?.scripture) ? (
                  <div className="space-y-4">
                    {dailyInspiration?.affirmation && (
                      <div className="rounded-xl p-4 bg-gradient-to-br from-[#CAAB84]/10 to-[#EAD8C2]/30 border border-[#CAAB84]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="h-4 w-4 text-[#CAAB84]" />
                          <span className="text-sm font-medium text-[#726158]">Today's Affirmation</span>
                        </div>
                        <p className="text-sm text-[#726158] italic leading-relaxed">"{dailyInspiration.affirmation}"</p>
                      </div>
                    )}
                    {dailyInspiration?.scripture && (
                      <div className="rounded-xl p-4 bg-gradient-to-br from-[#87A6A7]/10 to-[#EAD8C2]/20 border border-[#87A6A7]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen className="h-4 w-4 text-[#87A6A7]" />
                          <span className="text-sm font-medium text-[#726158]">Daily Scripture</span>
                        </div>
                        <p className="text-sm text-[#726158] italic leading-relaxed">
                          "{dailyInspiration.scripture.text || dailyInspiration.scripture}"
                        </p>
                        {dailyInspiration.scripture.verse && (
                          <p className="text-xs text-[#87A6A7] mt-2 font-medium">— {dailyInspiration.scripture.verse}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl p-4 bg-[#EAD8C2]/20 border border-[#EAD8C2] text-center">
                    <Sparkles className="h-6 w-6 text-[#CAAB84] mx-auto mb-2" />
                    <p className="text-sm text-[#AA9283]">Set your preferences in Settings to receive daily inspiration</p>
                    <Link to="/settings" onClick={() => setSidebarOpen(false)}>
                      <Button variant="link" className="text-[#CAAB84] text-sm mt-2 p-0 h-auto">
                        Go to Settings →
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-[#EAD8C2] space-y-2">
                <Link to="/dashboard" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#726158] bg-[#EAD8C2]/30">
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                </Link>
                <Link to="/chat" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Chat with Dea
                  </Button>
                </Link>
                <Link to="/reflections" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Reflections
                  </Button>
                </Link>
                <Link to="/settings" onClick={() => setSidebarOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-[#AA9283] hover:text-[#726158] hover:bg-[#EAD8C2]/50">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="w-full justify-start text-[#AA9283] hover:text-[#B1897C] hover:bg-[#B1897C]/10"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="bg-[#FFFCF7]/90 backdrop-blur-lg border-b border-[#EAD8C2] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[#AA9283] hover:text-[#726158]"
              data-testid="sidebar-toggle-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/">
              <img src={LOGO_NAV} alt="Dea" className="h-10 w-auto object-contain" />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {/* Current Date Display */}
            {currentDate && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EAD8C2]/50 border border-[#EAD8C2]">
                <Calendar className="h-4 w-4 text-[#CAAB84]" />
                <span className="text-sm text-[#726158]">{currentDate.date}</span>
              </div>
            )}
            <Link to="/chat">
              <Button className="btn-gold" data-testid="dashboard-chat-btn">
                <MessageSquare className="h-4 w-4 mr-2" />
                Chat with Dea
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Big Logo at top of page content */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center"
        >
          <img src={LOGO_HERO} alt="Dea" className="h-16 md:h-20 w-auto object-contain" />
        </motion.div>

        {/* Trial/Expired Trial Banners - only show for trial users */}
        <TrialBanner />
        <ExpiredTrialBanner />

        {/* Welcome with Date */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl text-[#726158] mb-2">
                Hey, <span className="text-[#CAAB84]">{user?.name}</span>
              </h1>
              <p className="text-[#AA9283]">
                {currentDay > 0 
                  ? `Day ${currentDay} of your glow up journey. Keep glowing, sis!`
                  : 'Ready to start your glow up?'}
              </p>
            </div>
            {/* Mobile Date Display */}
            {currentDate && (
              <div className="md:hidden flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FFFCF7] border border-[#EAD8C2]">
                <Calendar className="h-5 w-5 text-[#CAAB84]" />
                <div>
                  <p className="text-sm font-medium text-[#726158]">{currentDate.date}</p>
                  <p className="text-xs text-[#B1897C]">{currentDate.time} {currentDate.timezone}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Final Push Banner (Days 80-89) */}
          {currentDay >= 80 && currentDay < 90 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mt-4 bg-gradient-to-r from-[#CAAB84] to-[#AA9283] rounded-xl p-4 text-white"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-serif text-xl">🔥 THE FINAL PUSH!</p>
                  <p className="text-white/90 text-sm">
                    Only {90 - currentDay} days left! You're so close to the finish line. Let's make every day count!
                  </p>
                </div>
                <Link to="/chat" className="ml-auto">
                  <Button className="bg-white text-[#CAAB84] hover:bg-white/90">
                    Talk to Dea
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-6 card-hover"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#CAAB84]/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-[#CAAB84]" />
              </div>
              <span className="text-[#AA9283]">Current Day</span>
            </div>
            <p className="font-serif text-4xl text-[#726158]">{currentDay}</p>
            <p className="text-[#B1897C] text-sm mt-1">of your glow up</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card rounded-2xl p-6 card-hover"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#87A6A7]/10 flex items-center justify-center">
                <Target className="h-5 w-5 text-[#87A6A7]" />
              </div>
              <span className="text-[#AA9283]">Journey Progress</span>
            </div>
            <p className="font-serif text-4xl text-[#726158]">{progress}%</p>
            <Progress value={progress} className="mt-2 h-2" />
            <p className="text-[#B1897C] text-xs mt-1">
              {taskProgress.completed} of 90 days checked in
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card rounded-2xl p-6 card-hover"
            data-testid="days-with-dea-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#B8956A]/15 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-[#B8956A]" />
              </div>
              <span className="text-[#AA9283]">Days with Dea</span>
            </div>
            <p className="font-serif text-4xl text-[#726158]" data-testid="dea-streak-count">
              {deaStreak}
            </p>
            <p className="text-[#B1897C] text-sm mt-1">
              {deaStreak === 1 ? 'day streak' : 'day streak'}
            </p>
            <p className="text-[#B1897C] text-xs mt-1">
              {deaStreak === 0
                ? 'Start your streak today'
                : deaStreak >= 7
                  ? 'You\'re glowing — keep going!'
                  : 'Show up every day, sis'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-card rounded-2xl p-6 card-hover"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#585C4E]/10 flex items-center justify-center">
                <Trophy className="h-5 w-5 text-[#585C4E]" />
              </div>
              <span className="text-[#AA9283]">Milestones</span>
            </div>
            <p className="font-serif text-4xl text-[#726158]">
              {milestones.completed}<span className="text-xl text-[#B1897C]">/{milestones.total}</span>
            </p>
            <Progress value={milestones.progress_percent} className="mt-2 h-2" />
            <p className="text-[#B1897C] text-sm mt-1">weekly milestones achieved</p>
          </motion.div>
        </div>

        {/* My Glow Up Habits — Section 2 */}
        <HabitTracker journeyDay={currentDay} isTrialUser={isTrialUser || isExpiredTrial} />

        {/* Cycle Sync — moved to BOTTOM of dashboard per user request (Feb 2026).
            Rendered after the 12-Week Milestones section below. */}

        {/* Phase banner removed per user feedback (Feb 2026) — dashboard now flows
            directly from the habit tracker into "Your Glow Up Today". */}

        {/* This Week's Focus Section - Shows locked preview for trial users */}
        {/* Wrapper grid: 65% left (existing today content) / 35% right (Journal Preview) on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.85fr_1fr] gap-6 mt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="glass-card rounded-2xl p-6 relative"
          style={{ minHeight: (isTrialUser || isExpiredTrial) && !journey ? '280px' : 'auto' }}
        >
          {/* Lock overlay for trial/expired trial users - show even without journey for sneak peek */}
          {(isTrialUser || isExpiredTrial) && (
            <LockedSection 
              title="90-Day Roadmap" 
              description="Upgrade to unlock your personalized 12-week roadmap and track your full journey."
              previewType="roadmap"
            />
          )}
          
          {journey ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl text-[#726158]" data-testid="glow-up-today-heading">
                  Your <span className="text-[#B8956A]">Glow Up</span> Today
                </h3>
                <Link to="/chat">
                  <Button variant="ghost" size="sm" className="text-[#87A6A7] hover:text-[#87A6A7] hover:bg-[#87A6A7]/10">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Talk to Dea
                  </Button>
                </Link>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Left Side - Week Context */}
                <div className="space-y-4">
                  {/* Week Number & Phase Badge */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#CAAB84]/20 border border-[#CAAB84]/30">
                      <Target className="h-4 w-4 text-[#CAAB84]" />
                      <span className="font-medium text-[#726158]">Week {Math.ceil(currentDay / 7)} of 12</span>
                    </div>
                    <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                      journey.phase === 'alignment' ? 'bg-[#87A6A7]/20 text-[#87A6A7]' :
                      journey.phase === 'amplification' ? 'bg-[#CAAB84]/20 text-[#CAAB84]' :
                      'bg-[#AA9283]/20 text-[#AA9283]'
                    }`}>
                      {phaseInfo[journey.phase]?.name || 'Alignment Phase'}
                    </div>
                  </div>
                  
                  {/* This Week's Milestone Title */}
                  <div className="bg-gradient-to-br from-[#EAD8C2]/40 to-[#EAD8C2]/20 rounded-xl p-5 border border-[#EAD8C2]">
                    <p className="text-xs text-[#AA9283] uppercase tracking-wide mb-2">This Week's Milestone</p>
                    <h4 className="font-serif text-xl text-[#726158] leading-relaxed">
                      {journey.milestones?.[`week_${Math.ceil(currentDay / 7)}`]?.title || 'Focus on your goals'}
                    </h4>
                  </div>
                  
                  {/* Today's Focus */}
                  <div className="flex items-center gap-3 text-sm text-[#AA9283]">
                    <Sparkles className="h-4 w-4 text-[#CAAB84]" />
                    <span>Today's Focus: {phaseInfo[journey.phase]?.focus || 'Clarity, Habits & Mindset'}</span>
                  </div>
                </div>
                
                {/* Right Side - Tasks Grid */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-[#726158]">Your Glow Up Tasks</p>
                    <span className="text-xs text-[#AA9283]">
                      {taskProgress.completed}/{taskProgress.total} completed
                    </span>
                  </div>
                  
                  {pendingTasks.length > 0 ? (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {pendingTasks.slice(0, 4).map((task, index) => (
                        <button
                          key={task.id || index}
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API}/tasks/${task.id}`,
                                { completed: true },
                                { headers: getAuthHeader() }
                              );
                              setPendingTasks(prev => prev.filter(t => t.id !== task.id));
                              setTaskProgress(prev => ({
                                ...prev,
                                completed: prev.completed + 1,
                                progress_percent: Math.round(((prev.completed + 1) / prev.total) * 100)
                              }));
                              toast.success('Task completed! 🎉');
                            } catch (error) {
                              toast.error('Failed to complete task');
                            }
                          }}
                          className="flex items-start gap-3 p-3 rounded-xl bg-[#FFFCF7] border border-[#EAD8C2] hover:border-[#87A6A7] hover:bg-[#87A6A7]/5 transition-all text-left group"
                          data-testid={`task-${index}`}
                        >
                          <div className="mt-0.5">
                            <Circle className="h-4 w-4 text-[#B1897C] group-hover:text-[#87A6A7]" />
                          </div>
                          <span className="text-sm text-[#726158] line-clamp-2">{task.description}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {/* Empty state with suggestions */}
                      <div className="col-span-2 text-center py-6 bg-[#EAD8C2]/20 rounded-xl border border-dashed border-[#EAD8C2]">
                        <CheckCircle className="h-8 w-8 text-[#87A6A7] mx-auto mb-2" />
                        <p className="text-sm text-[#AA9283] mb-3">All caught up! No pending tasks.</p>
                        <Link to="/chat">
                          <Button variant="ghost" size="sm" className="text-[#CAAB84] hover:text-[#CAAB84] hover:bg-[#CAAB84]/10">
                            Ask Dea for your next task →
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {pendingTasks.length > 4 && (
                    <p className="text-xs text-[#B1897C] mt-3 text-center">
                      +{pendingTasks.length - 4} more tasks
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* No Journey Started State */
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#CAAB84]/20 flex items-center justify-center mx-auto mb-4">
                <Rocket className="h-8 w-8 text-[#CAAB84]" />
              </div>
              
              {/* Show different UI if Dea has already created a roadmap */}
              {pendingRoadmap ? (
                <>
                  <h3 className="font-serif text-xl text-[#726158] mb-2">Your Roadmap is Ready! 🎉</h3>
                  <p className="text-[#AA9283] mb-4 max-w-md mx-auto">
                    Dea has created your personalized 90-day glow up journey based on your conversation.
                  </p>
                  {pendingRoadmap.main_goal && (
                    <div className="bg-[#EAD8C2]/30 rounded-xl p-4 mb-6 max-w-md mx-auto">
                      <p className="text-xs text-[#AA9283] mb-1">Your 90-Day Glow Up Goal:</p>
                      <p className="text-[#726158] font-medium">{pendingRoadmap.main_goal}</p>
                    </div>
                  )}
                  <div className="flex gap-3 justify-center flex-wrap">
                    <Button 
                      className="btn-gold"
                      onClick={handleStartFromChat}
                      disabled={startingJourney}
                      data-testid="start-from-chat-btn"
                    >
                      {startingJourney ? <span className="spinner" /> : (
                        <>
                          <Rocket className="h-4 w-4 mr-2" />
                          Start My Journey Now
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-[#EAD8C2] text-[#AA9283]"
                      onClick={() => setShowJourneyModal(true)}
                    >
                      Customize My Goal
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-serif text-xl text-[#726158] mb-2">Ready to Glow Up?</h3>
                  <p className="text-[#AA9283] mb-6 max-w-md mx-auto">
                    Start your 90-day glow up journey. Dea will guide you through every phase — clarity, habits, mindset, strategy, and well-being.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Link to="/chat">
                      <Button className="btn-gold">
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Chat with Dea to Get Started
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="border-[#EAD8C2] text-[#AA9283]"
                      onClick={() => setShowJourneyModal(true)}
                      data-testid="start-journey-cta"
                    >
                      <Rocket className="h-4 w-4 mr-2" />
                      Set My Own Goal
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </motion.div>
        {/* Journal Preview — right column (35% desktop, stacked below on mobile) */}
        <JournalPreview />
        </div>

        {/* Your 12-Week Milestones Section - Show for trial users even without journey for sneak peek */}
        {(journey && milestones.milestones.length > 0) || isTrialUser || isExpiredTrial ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
            className="glass-card rounded-2xl p-6 mt-8 relative"
            style={{ minHeight: (isTrialUser || isExpiredTrial) ? '280px' : 'auto' }}
          >
            {/* Lock overlay for trial/expired trial users */}
            {(isTrialUser || isExpiredTrial) && (
              <LockedSection 
                title="12-Week Milestones" 
                description="Upgrade to track your weekly milestones and celebrate your progress."
                previewType="milestones"
              />
            )}
            
            {journey && milestones.milestones.length > 0 && (
              <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Trophy className="h-5 w-5 text-[#CAAB84]" />
                <h3 className="font-serif text-xl text-[#726158]">Your 12-Week Glow Up Milestones</h3>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/journey/sync-roadmap`, {}, { headers: getAuthHeader() });
                      toast.success(res.data.message);
                      // Refresh milestones
                      const milestonesRes = await axios.get(`${API}/journey/milestones`, { headers: getAuthHeader() });
                      setMilestones(milestonesRes.data);
                      // Refresh journey too
                      const journeyRes = await axios.get(`${API}/journey`, { headers: getAuthHeader() });
                      setJourney(journeyRes.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to sync roadmap');
                    }
                  }}
                  className="text-[#87A6A7] hover:text-[#87A6A7] hover:bg-[#87A6A7]/10"
                  data-testid="sync-roadmap-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Sync from Chat
                </Button>
                <div className="text-sm text-[#AA9283]">
                  {milestones.completed} of {milestones.total} completed
                </div>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {milestones.milestones.map((milestone) => {
                const isCurrentWeek = journey?.week === milestone.week;
                const isPastWeek = journey?.week > milestone.week;
                
                return (
                  <button
                    key={milestone.week}
                    onClick={async () => {
                      try {
                        const newStatus = !milestone.completed;
                        await axios.put(
                          `${API}/journey/milestones/${milestone.week}`,
                          { completed: newStatus },
                          { headers: getAuthHeader() }
                        );
                        // Update local state
                        setMilestones(prev => ({
                          ...prev,
                          milestones: prev.milestones.map(m => 
                            m.week === milestone.week ? { ...m, completed: newStatus } : m
                          ),
                          completed: newStatus ? prev.completed + 1 : prev.completed - 1,
                          progress_percent: Math.round(((newStatus ? prev.completed + 1 : prev.completed - 1) / prev.total) * 100)
                        }));
                        if (newStatus) {
                          toast.success(`Week ${milestone.week} milestone completed! 🎉`);
                        } else {
                          toast.info(`Week ${milestone.week} milestone unchecked`);
                        }
                      } catch (error) {
                        toast.error('Failed to update milestone');
                      }
                    }}
                    className={`text-left p-4 rounded-xl transition-all cursor-pointer ${
                      milestone.completed
                        ? 'bg-[#87A6A7]/20 border-2 border-[#87A6A7]/50 hover:bg-[#87A6A7]/30'
                        : isCurrentWeek
                        ? 'bg-[#CAAB84]/20 border-2 border-[#CAAB84] hover:bg-[#CAAB84]/30'
                        : isPastWeek
                        ? 'bg-[#EAD8C2]/50 border-2 border-[#EAD8C2] hover:bg-[#EAD8C2]/70'
                        : 'bg-[#EAD8C2]/30 border-2 border-transparent hover:border-[#EAD8C2]'
                    }`}
                    data-testid={`milestone-week-${milestone.week}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-medium ${
                        isCurrentWeek ? 'text-[#CAAB84]' : 'text-[#AA9283]'
                      }`}>
                        Week {milestone.week}
                        {isCurrentWeek && <span className="ml-1">(Current)</span>}
                      </span>
                      {milestone.completed ? (
                        <CheckCircle className="h-5 w-5 text-[#87A6A7]" />
                      ) : (
                        <Circle className={`h-5 w-5 ${isCurrentWeek ? 'text-[#CAAB84]' : 'text-[#B1897C]'}`} />
                      )}
                    </div>
                    <p className={`text-sm font-medium ${
                      milestone.completed ? 'text-[#87A6A7]' : 'text-[#726158]'
                    }`}>
                      {milestone.title}
                    </p>
                  </button>
                );
              })}
            </div>
            
            <p className="text-xs text-[#B1897C] mt-4 text-center">
              Click on a milestone to check or uncheck it
            </p>
              </>
            )}
          </motion.div>
        ) : null}

        {/* Recent Wins */}
        {journey?.wins?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="glass-card rounded-2xl p-6"
          >
            <h3 className="font-serif text-xl text-[#726158] mb-4">Recent Wins</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {journey.wins.slice(-6).map((win) => (
                <div key={win.id} className="bg-[#EAD8C2]/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="h-4 w-4 text-[#CAAB84]" />
                    <span className="text-[#AA9283] text-xs">Day {win.day}</span>
                  </div>
                  <p className="text-[#726158] text-sm">{win.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Cycle Sync — moved to very bottom of dashboard per user request (Feb 2026).
            For trial/expired-trial users: render a LOCKED preview here (Feb 2026 update). */}
        {(isTrialUser || isExpiredTrial) && !cyclePhase && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
            className="glass-card rounded-2xl p-6 border border-[#EAD8C2] mt-8 relative overflow-hidden"
            style={{ minHeight: '280px' }}
            data-testid="cycle-sync-locked"
          >
            <div className="flex items-center gap-2 mb-6">
              <Moon className="h-5 w-5 text-[#AA9283]" />
              <h2 className="font-serif text-xl text-[#726158]">Your Cycle Sync</h2>
            </div>
            {/* Blurred preview */}
            <div className="absolute inset-0 blur-[6px] opacity-50 pointer-events-none p-6 pt-16">
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="aspect-square max-w-xs rounded-2xl bg-gradient-to-br from-[#EAD8C2]/40 via-[#CAAB84]/30 to-[#AA9283]/20 flex items-center justify-center">
                  <Moon className="h-16 w-16 text-[#AA9283]/40" />
                </div>
                <div className="space-y-3">
                  <div className="h-16 rounded-xl bg-[#EAD8C2]/40" />
                  <div className="h-14 rounded-xl bg-[#FFFCF7] border border-[#EAD8C2]" />
                  <div className="h-14 rounded-xl bg-[#FFFCF7] border border-[#EAD8C2]" />
                </div>
              </div>
            </div>
            {/* Lock overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#FFFCF7]/70 via-[#FFFCF7]/90 to-[#FFFCF7]/70 flex items-center justify-center">
              <div className="text-center p-6 bg-white/80 rounded-2xl shadow-lg border border-[#EAD8C2] max-w-xs">
                <div className="w-14 h-14 rounded-full bg-[#CAAB84]/20 flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-7 w-7 text-[#CAAB84]" />
                </div>
                <h4 className="font-serif text-lg text-[#726158] mb-2">Cycle Sync</h4>
                <p className="text-sm text-[#AA9283] mb-4">Work with your body, not against it. Unlock cycle-aware task recommendations.</p>
                <Link to="/checkout?upgrade=true">
                  <Button className="btn-gold w-full" data-testid="cycle-sync-upgrade-btn">
                    Unlock Full Access — ${upgradePrice}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {cyclePhase && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
            className="glass-card rounded-2xl p-6 border border-[#EAD8C2] mt-8"
          >
            <div className="flex items-center gap-2 mb-6">
              <Moon className="h-5 w-5 text-[#AA9283]" />
              <h2 className="font-serif text-xl text-[#726158]">Your Cycle Sync</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left: Calendar Visual */}
              <CycleCalendar currentDay={cyclePhase.day} phase={cyclePhase.phase} />

              {/* Right: Current Phase Details */}
              <div className="space-y-4">
                {/* Current Phase Header */}
                <div className={`rounded-2xl p-5 ${cyclePhaseConfig[cyclePhase.phase]?.lightBg} border ${cyclePhaseConfig[cyclePhase.phase]?.borderColor}`}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className={`w-14 h-14 rounded-full ${cyclePhaseConfig[cyclePhase.phase]?.bgColor} flex items-center justify-center text-white shadow-lg`}>
                      <div className="text-center">
                        <span className="text-xl font-bold block">{cyclePhase.day}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className={`font-serif text-xl font-semibold ${cyclePhaseConfig[cyclePhase.phase]?.textColor}`}>
                        {cyclePhase.name} Phase
                      </h3>
                      <p className="text-sm text-[#AA9283]">Day {cyclePhase.day} of 28</p>
                    </div>
                  </div>
                </div>

                {/* Energy Level */}
                <div className="bg-[#FFFCF7] rounded-xl p-4 border border-[#EAD8C2]">
                  <p className="text-sm text-[#AA9283] mb-1">Energy Level</p>
                  <p className="font-medium text-[#726158]">{cyclePhase.energy}</p>
                </div>

                {/* Recommended Tasks */}
                <div className="bg-[#FFFCF7] rounded-xl p-4 border border-[#EAD8C2]">
                  <p className="text-sm text-[#AA9283] mb-1">Best Tasks for Today</p>
                  <p className="font-medium text-[#726158]">{cyclePhase.tasks || cyclePhase.focus}</p>
                </div>

                {/* Full Recommendation */}
                <div className="bg-[#EAD8C2]/30 rounded-xl p-4">
                  <p className="text-sm text-[#726158]">{cyclePhase.recommendations}</p>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Link to="/chat" className="flex-1">
                    <Button className="w-full btn-outline-gold" size="sm">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Ask Dea for Tasks
                    </Button>
                  </Link>
                  <Link to="/reflections" className="flex-1">
                    <Button variant="outline" className="w-full border-[#EAD8C2] text-[#AA9283]" size="sm">
                      <BookOpen className="h-4 w-4 mr-2" />
                      Journal
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
    </>
  );
}
